import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class SoundService {
  private ctx: AudioContext | null = null;
  private enabled = true;

  private getContext(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext();
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    return this.ctx;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  // ── Basic tone generator ──
  private playTone(freq: number, duration: number, type: OscillatorType = 'sine', volume = 0.3): void {
    if (!this.enabled) return;
    try {
      const ctx = this.getContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      gain.gain.setValueAtTime(volume, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + duration);
    } catch {
      // Silently fail if audio context is not available
    }
  }

  // ── Multi-tone sequence ──
  private playSequence(tones: { freq: number; start: number; dur: number; type?: OscillatorType; vol?: number }[]): void {
    if (!this.enabled) return;
    try {
      const ctx = this.getContext();
      for (const t of tones) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = t.type || 'sine';
        osc.frequency.setValueAtTime(t.freq, ctx.currentTime + t.start);
        gain.gain.setValueAtTime(t.vol ?? 0.25, ctx.currentTime + t.start);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t.start + t.dur);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + t.start);
        osc.stop(ctx.currentTime + t.start + t.dur);
      }
    } catch {}
  }

  // ═══════════════════════════════
  // PUBLIC SOUND EFFECTS
  // ═══════════════════════════════

  /** Generic tap / click feedback */
  tap(): void {
    this.playTone(800, 0.08, 'sine', 0.15);
  }

  /** Card flip sound */
  flip(): void {
    this.playTone(500, 0.1, 'sine', 0.12);
  }

  /** Correct match / hit */
  hit(): void {
    this.playSequence([
      { freq: 600, start: 0, dur: 0.08, vol: 0.2 },
      { freq: 900, start: 0.06, dur: 0.12, vol: 0.25 },
    ]);
  }

  /** Wrong / miss */
  miss(): void {
    this.playSequence([
      { freq: 300, start: 0, dur: 0.15, type: 'square', vol: 0.12 },
      { freq: 200, start: 0.1, dur: 0.2, type: 'square', vol: 0.1 },
    ]);
  }

  /** Score point (ascending blip) */
  score(): void {
    this.playSequence([
      { freq: 660, start: 0, dur: 0.06, vol: 0.2 },
      { freq: 880, start: 0.05, dur: 0.08, vol: 0.22 },
    ]);
  }

  /** Combo multiplier */
  combo(): void {
    this.playSequence([
      { freq: 523, start: 0, dur: 0.06, vol: 0.2 },
      { freq: 659, start: 0.05, dur: 0.06, vol: 0.22 },
      { freq: 784, start: 0.1, dur: 0.06, vol: 0.24 },
      { freq: 1047, start: 0.15, dur: 0.12, vol: 0.28 },
    ]);
  }

  /** Game start countdown beep */
  countdown(): void {
    this.playTone(440, 0.12, 'sine', 0.15);
  }

  /** Last 5 seconds warning beep */
  warning(): void {
    this.playTone(880, 0.15, 'square', 0.12);
  }

  /** Victory / game completed fanfare */
  victory(): void {
    this.playSequence([
      { freq: 523, start: 0, dur: 0.15, vol: 0.25 },
      { freq: 659, start: 0.12, dur: 0.15, vol: 0.25 },
      { freq: 784, start: 0.24, dur: 0.15, vol: 0.28 },
      { freq: 1047, start: 0.36, dur: 0.3, vol: 0.3 },
    ]);
  }

  /** New record celebration */
  record(): void {
    this.playSequence([
      { freq: 523, start: 0, dur: 0.1, vol: 0.25 },
      { freq: 659, start: 0.08, dur: 0.1, vol: 0.25 },
      { freq: 784, start: 0.16, dur: 0.1, vol: 0.28 },
      { freq: 1047, start: 0.24, dur: 0.15, vol: 0.3 },
      { freq: 1319, start: 0.36, dur: 0.15, vol: 0.28 },
      { freq: 1568, start: 0.48, dur: 0.3, vol: 0.3 },
    ]);
  }

  /** Game over / fail */
  gameOver(): void {
    this.playSequence([
      { freq: 400, start: 0, dur: 0.2, type: 'triangle', vol: 0.2 },
      { freq: 350, start: 0.15, dur: 0.2, type: 'triangle', vol: 0.18 },
      { freq: 300, start: 0.3, dur: 0.3, type: 'triangle', vol: 0.15 },
    ]);
  }

  /** Balloon pop */
  pop(): void {
    if (!this.enabled) return;
    try {
      const ctx = this.getContext();
      // White noise burst for pop effect
      const bufferSize = ctx.sampleRate * 0.06;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
      }
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06);
      source.connect(gain);
      gain.connect(ctx.destination);
      source.start();
    } catch {}
  }

  /** Simon tone - each color has a distinct pitch */
  simonTone(colorIndex: number): void {
    const freqs = [261, 329, 392, 523, 587, 659, 784, 880, 988];
    const freq = freqs[colorIndex % freqs.length];
    this.playTone(freq, 0.3, 'sine', 0.25);
  }

  /** Reaction - the green "GO!" sound */
  go(): void {
    this.playSequence([
      { freq: 880, start: 0, dur: 0.08, vol: 0.3 },
      { freq: 1100, start: 0.06, dur: 0.12, vol: 0.3 },
    ]);
  }

  /** Slide tile move */
  slide(): void {
    this.playTone(350, 0.06, 'sine', 0.1);
  }
}
